import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lightbulb, Plus, Trash2 } from "lucide-react";
import { api } from "../../lib/api";

const PAGE = "a-revoir";
const KEY = "items";

export default function ARevoir() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/client/submissions/${PAGE}`).then(({ data }) => {
      const raw = data?.[KEY];
      if (raw) {
        try { setItems(JSON.parse(raw)); } catch (_) {}
        setLoaded(true);
        return;
      }
      // Migration : anciennes données encore dans le navigateur (localStorage)
      const ls = localStorage.getItem("a-revoir-items");
      if (ls) {
        try {
          const arr = JSON.parse(ls);
          setItems(arr);
          api.put(`/client/submissions/${PAGE}`, { key: KEY, value: ls }).catch(() => {});
        } catch (_) {}
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const persist = (next) => {
    setItems(next);
    api.put(`/client/submissions/${PAGE}`, { key: KEY, value: JSON.stringify(next) }).catch(() => {});
  };

  const add = () => {
    if (!draft.trim()) return;
    const text = draft.trim();
    persist([...items, { id: Date.now(), text }]);
    api.post("/client/a-revoir/notify", { text }).catch(() => {});
    setDraft("");
  };

  const remove = (id) => persist(items.filter((i) => i.id !== id));

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl space-y-6">
      <div>
        <p className="eyebrow">Ensemble</p>
        <h1 className="mt-2 font-sans text-3xl font-medium text-ink sm:text-4xl">À revoir ensemble</h1>
      </div>

      <div data-testid="arevoir-intro-note" className="flex gap-4 rounded-2xl border border-bordeaux-border bg-bordeaux-tint/30 p-5 sm:p-6">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-bordeaux">
          <Lightbulb size={20} />
        </span>
        <p className="font-light leading-relaxed text-ink-soft">
          Tu peux me partager ici tout ce qu'on va pouvoir revoir ensemble (page de vente, mails, contenus IG…), n'hésite pas
          non plus à m'expliquer ce que tu as fait et pourquoi.
        </p>
      </div>

      <div className="rounded-2xl border border-line bg-white p-5 shadow-card">
        <label className="text-sm font-medium text-ink">Ajouter un élément</label>
        <div className="mt-2 flex flex-col gap-3 sm:flex-row">
          <textarea
            data-testid="arevoir-input"
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Colle un lien ou décris ce que tu veux qu'on revoie ensemble…"
            className="flex-1 rounded-xl border border-line bg-cream/40 p-3 text-sm outline-none transition focus:border-bordeaux focus:bg-white"
          />
          <button
            data-testid="arevoir-add"
            onClick={add}
            className="flex items-center justify-center gap-2 rounded-full bg-bordeaux px-5 py-2.5 text-sm font-medium text-white transition hover:bg-bordeaux-dark sm:self-start"
          >
            <Plus size={16} /> Ajouter
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {loaded && items.length === 0 && (
          <p className="text-sm text-ink-muted">Rien pour le moment. Ajoute un premier élément ci-dessus 👆</p>
        )}
        {items.map((i) => (
          <div key={i.id} data-testid={`arevoir-item-${i.id}`} className="flex items-start justify-between gap-4 rounded-xl border border-line bg-white p-4 shadow-card">
            <p className="whitespace-pre-wrap break-words text-sm text-ink-soft">{i.text}</p>
            <button data-testid={`arevoir-remove-${i.id}`} onClick={() => remove(i.id)} className="shrink-0 text-ink-muted transition hover:text-bordeaux">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
